/**
 * "Resolve with a fix note" in the Feedback inbox (9 Oct): the Open / Resolved
 * / All chips, and the Status and Fix cells every view adds to its table.
 *
 * Rusty: the inbox should show what is still open. Each item (a 👍/👎 row, an
 * auto-logged error group, a Leadership report question) is resolved with a
 * short note, usually the PR that fixed it ("PR #83 – retry before logging"),
 * and can be reopened. An error that happens again after its fix reopens by
 * itself and is marked "came back", pointing at the note.
 */

import {
  Badge,
  Button,
  Chip,
  ChipGroup,
  Dialog,
  Field,
  Textarea,
  Tooltip,
  Typography,
} from "@norican/react";
import { type ReactNode, useState } from "react";

import { EmptyStateBlock, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { FeedbackItemKind, Resolution, ResolutionCounts } from "@/lib/api";

import { useRecentFixes, useReopenFeedback, useResolveFeedback } from "./api";
import {
  applyQuickPick,
  chipCount,
  FIX_NOTE_MAX,
  normaliseFixNote,
  prQuickPicks,
  resolutionState,
  STATUS_FILTERS,
  type StatusFilter,
} from "./resolution-helpers";

export interface StatusFilterChipsProps {
  id: string;
  value: StatusFilter;
  counts: ResolutionCounts | null | undefined;
  onChange: (next: StatusFilter) => void;
}

/** Open / Resolved / All, each with its count. */
export function StatusFilterChips({ id, value, counts, onChange }: StatusFilterChipsProps) {
  const { t } = useT("assistant");
  return (
    <span className="flex items-center gap-2">
      <Typography variant="label" id={id}>
        {t("feedbackAdmin.resolution.filterLabel")}
      </Typography>
      <ChipGroup.Root
        aria-labelledby={id}
        value={[value]}
        onValueChange={(next) => {
          const picked = next[0] as StatusFilter | undefined;
          if (picked !== undefined && picked !== value) {
            onChange(picked);
          }
        }}
      >
        {STATUS_FILTERS.map((option) => (
          <Chip key={option} value={option} size="sm">
            {t(`feedbackAdmin.resolution.filter.${option}`, {
              count: chipCount(option, counts),
            })}
          </Chip>
        ))}
      </ChipGroup.Root>
    </span>
  );
}

/**
 * The empty table under a status filter: "nothing open" when everything is
 * resolved, "nothing resolved yet", else the view's own empty state.
 */
export function EmptyForStatus({
  status,
  counts,
  children,
}: {
  status: StatusFilter;
  counts: ResolutionCounts | null | undefined;
  children: ReactNode;
}) {
  const { t } = useT("assistant");
  if (status === "open" && (counts?.resolved ?? 0) > 0) {
    return (
      <EmptyStateBlock
        title={t("feedbackAdmin.resolution.empty.allDone")}
        description={t("feedbackAdmin.resolution.empty.allDoneHint")}
      />
    );
  }
  if (status === "resolved" && (counts?.open ?? 0) > 0) {
    return (
      <EmptyStateBlock
        title={t("feedbackAdmin.resolution.empty.noneResolved")}
        description={t("feedbackAdmin.resolution.empty.noneResolvedHint")}
      />
    );
  }
  return <>{children}</>;
}

/**
 * The Status column, kept narrow (12 Oct): ✅ (the date on hover), "Came back",
 * or Open.
 */
export function ResolutionStatusCell({
  resolution,
}: {
  resolution: Resolution | null | undefined;
}) {
  const { t } = useT("assistant");
  const { formatDate } = useDateFormatter();
  const state = resolutionState(resolution);
  if (state === "resolved") {
    const label = resolution?.resolved_at
      ? `${t("feedbackAdmin.resolution.status.resolved")} · ${formatDate(resolution.resolved_at)}`
      : t("feedbackAdmin.resolution.status.resolved");
    return (
      <span role="img" aria-label={label} title={label}>
        ✅
      </span>
    );
  }
  if (state === "cameBack") {
    return (
      <Badge
        tone="danger"
        variant="subtle"
        size="sm"
        title={resolution?.came_back_at ? formatDate(resolution.came_back_at) : undefined}
      >
        {t("feedbackAdmin.resolution.status.cameBack")}
      </Badge>
    );
  }
  return (
    <Badge tone="neutral" variant="outline" size="sm">
      {t("feedbackAdmin.resolution.status.open")}
    </Badge>
  );
}

export interface ResolutionFixCellProps {
  kind: FeedbackItemKind;
  itemKey: string;
  resolution: Resolution | null | undefined;
  /** What the dialog says it resolves ("CLIENT_NETWORK_ERROR · /tickets"). */
  subject: string;
}

/** The fix note on one line; the whole note, who and when in a tooltip. */
function FixNote({ text, detail }: { text: string; detail: string | null }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        render={<span tabIndex={0} />}
        className="min-w-0 flex-1 truncate text-start"
      >
        {text}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner sideOffset={4}>
          <Tooltip.Popup className="max-w-xs break-words whitespace-pre-wrap">
            {text}
            {detail ? `\n${detail}` : ""}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * The Fix column on one line (12 Oct): the note, short, with the rest in a
 * tooltip, then "Reopen" or "Resolve…".
 */
export function ResolutionFixCell({ kind, itemKey, resolution, subject }: ResolutionFixCellProps) {
  const { t } = useT("assistant");
  const { formatDate } = useDateFormatter();
  const reopen = useReopenFeedback();
  const [open, setOpen] = useState(false);
  const state = resolutionState(resolution);
  const note = resolution?.fix_note ?? "";
  const who = [
    resolution?.resolved_by ?? "",
    resolution?.resolved_at ? formatDate(resolution.resolved_at) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <span className="flex w-full min-w-0 items-center gap-1">
      {state === "resolved" ? (
        note ? (
          <FixNote text={note} detail={who || null} />
        ) : (
          <span className="flex-1" />
        )
      ) : state === "cameBack" ? (
        <FixNote
          text={t("feedbackAdmin.resolution.cameBackAfter", { note: note || "–" })}
          detail={null}
        />
      ) : (
        <span className="flex-1" />
      )}
      {state === "resolved" ? (
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          className="shrink-0"
          loading={reopen.isPending}
          onClick={() => reopen.mutate({ kind, key: itemKey })}
        >
          {t("feedbackAdmin.resolution.reopen")}
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="shrink-0" onClick={() => setOpen(true)}>
          {t("feedbackAdmin.resolution.resolve")}
        </Button>
      )}
      {reopen.isError ? (
        <Badge
          role="alert"
          tone="danger"
          variant="subtle"
          size="sm"
          className="shrink-0"
          aria-label={t("feedbackAdmin.resolution.failed")}
          title={t("feedbackAdmin.resolution.failed")}
        >
          !
        </Badge>
      ) : null}
      <ResolveDialog
        open={open}
        onClose={() => setOpen(false)}
        kind={kind}
        itemKey={itemKey}
        subject={subject}
        initialNote={state === "cameBack" ? "" : note}
      />
    </span>
  );
}

interface ResolveDialogProps {
  open: boolean;
  onClose: () => void;
  kind: FeedbackItemKind;
  itemKey: string;
  subject: string;
  initialNote: string;
}

function ResolveDialog({ open, onClose, kind, itemKey, subject, initialNote }: ResolveDialogProps) {
  const { t } = useT("assistant");
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" className="flex flex-col">
          <Dialog.Header>
            <Dialog.Title>{t("feedbackAdmin.resolution.dialog.title")}</Dialog.Title>
            <Dialog.CloseButton label={t("feedbackAdmin.resolution.dialog.cancel")} />
          </Dialog.Header>
          {open ? (
            <ResolveBody
              kind={kind}
              itemKey={itemKey}
              subject={subject}
              initialNote={initialNote}
              onDone={onClose}
            />
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ResolveBody({
  kind,
  itemKey,
  subject,
  initialNote,
  onDone,
}: Omit<ResolveDialogProps, "open" | "onClose"> & { onDone: () => void }) {
  const { t } = useT("assistant");
  const resolve = useResolveFeedback();
  const recent = useRecentFixes(true);
  const [note, setNote] = useState(initialNote);
  const picks = prQuickPicks(recent.data?.pull_requests ?? [], recent.data?.notes ?? []);

  function submit() {
    resolve.mutate({ kind, key: itemKey, fixNote: normaliseFixNote(note) }, { onSuccess: onDone });
  }

  return (
    <>
      <Dialog.Body className="flex flex-col gap-3">
        {subject ? (
          <Typography variant="body-sm" tone="muted" className="break-words">
            {subject}
          </Typography>
        ) : null}
        <Field
          name="fix-note"
          label={t("feedbackAdmin.resolution.dialog.noteLabel")}
          hint={t("feedbackAdmin.resolution.dialog.noteHint")}
        >
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("feedbackAdmin.resolution.dialog.placeholder")}
            rows={2}
            autoResize
            maxLength={FIX_NOTE_MAX}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="caption" tone="muted">
            {t("feedbackAdmin.resolution.dialog.quickPick")}
          </Typography>
          {/* "PR #" starts an empty note; a recent PR leads whatever is written. */}
          {[...(note.trim() === "" ? ["PR #"] : []), ...picks].map((pick) => (
            <Button
              key={pick}
              variant="outline"
              tone="neutral"
              size="sm"
              type="button"
              onClick={() =>
                setNote((current) => (pick === "PR #" ? "PR #" : applyQuickPick(current, pick)))
              }
            >
              {pick}
            </Button>
          ))}
        </div>
        {resolve.isError ? (
          <Typography variant="caption" className="text-danger">
            {t("feedbackAdmin.resolution.failed")}
          </Typography>
        ) : null}
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Close render={<Button variant="outline" tone="neutral" type="button" />}>
          {t("feedbackAdmin.resolution.dialog.cancel")}
        </Dialog.Close>
        <Button loading={resolve.isPending} onClick={submit}>
          {t("feedbackAdmin.resolution.dialog.confirm")}
        </Button>
      </Dialog.Footer>
    </>
  );
}
